
import { mdiClockOutline, mdiClose, mdiInformationOutline } from '@mdi/js'
import Icon from '@mdi/react'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Tooltip from '@mui/material/Tooltip'
import EmojiPicker from 'components/EmojiPicker'
import * as React from 'react'
import { useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Subject } from 'rxjs'
import { debounceTime, tap } from 'rxjs/operators'
import {
  removeComment,
  removeMultipostComment,
  setComment,
  setMultipostComment,
  setMultipostCommentDelay
} from 'services/compose'
import { composerResetKeySelector } from 'services/compose/selectors'
import { isPresetCommentDelay } from 'services/compose/utils'
import { checkFeatureAvailability, getProductUpgradeByFeature, promptUpgrade } from 'services/product'
import {
  BRAND_BLUESKY,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_THREADS,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  FEATURE_POST_COMMENTS
} from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import styles from './CommentTextBox.pcss'

export const DELAY_OPTIONS: Record<string, string> = {
  '0': 'No delay',
  '1': '1 to 10 min (random time)',
  '10': '10 to 20 min (random time)'
}

const CUSTOM_DELAY_VALUE = 'custom'
const MAX_CUSTOM_DELAY_MINUTES = 1000
const DELAY_WINDOW_MINUTES = 10
const MIN_CUSTOM_DELAY_MINUTES = 1
const DEBOUNCE_TIME = 200
const DEFAULT_HEIGHT = 30

const isPresetDelay = isPresetCommentDelay

const formatCustomDelayRange = (delay: number) =>
  `${delay} to ${delay + DELAY_WINDOW_MINUTES} min (random time)`

export const formatCommentDelay = (delay: number) =>
  isPresetDelay(delay) ? DELAY_OPTIONS[String(delay)] : formatCustomDelayRange(delay)
export const formatCommentDelayShort = (delay: number) => formatCommentDelay(delay).replace(' min (random time)', ' min')

interface CommentTextBoxProps {
  initialValue: string
  network: typeof BRAND_INSTAGRAM
  | typeof BRAND_FACEBOOK
  | typeof BRAND_YOUTUBE
  | typeof BRAND_LINKEDIN
  | typeof BRAND_BLUESKY
  | typeof BRAND_THREADS
  | typeof BRAND_TWITTER
  className?: string
  actionsClassName?: string
  multiPostId?: string
  delay?: number
  maxLength?: number
  commentId: string
  showRemoveButton?: boolean
  isFirstComment?: boolean
}

export function CommentTextBox(props: CommentTextBoxProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const composerKey = useSelector(composerResetKeySelector)
  const {
    initialValue,
    network,
    multiPostId,
    commentId,
    delay,
    maxLength,
    className,
    showRemoveButton,
    isFirstComment = false
  } = props
  const [text, setText] = useState(initialValue)
  const change$ = useRef<Subject<string>>(undefined)
  const inputElementRef = useRef<HTMLTextAreaElement>(null)
  const wasFocusedRef = useRef(false)
  const didMountRef = useRef(false)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)

  const commentsAllowed = useMemo(() => dispatch(checkFeatureAvailability(FEATURE_POST_COMMENTS, true)), [dispatch])
  const commentsProductUpgrade = useMemo(() => dispatch(getProductUpgradeByFeature(FEATURE_POST_COMMENTS)), [dispatch])

  // EXPL: Block input whenever the feature is unavailable, independent of whether an
  // upgrade product exists. Surface the upgrade prompt only when one is configured.
  // Returns true when the input should be blocked.
  const blockInput = React.useCallback(() => {
    if (commentsAllowed) {
      return false
    }
    if (commentsProductUpgrade) {
      dispatch(promptUpgrade({ feature: FEATURE_POST_COMMENTS, product: commentsProductUpgrade.handles.annual }))
    }
    return true
  }, [commentsAllowed, commentsProductUpgrade, dispatch])

  React.useEffect(() => {
    change$.current = new Subject()
    change$.current
      .pipe(tap(value => setText(value))).pipe(
        debounceTime(DEBOUNCE_TIME))
      .subscribe(value => {
        if (multiPostId) {
          dispatch(setMultipostComment({
            postId: multiPostId,
            comment: { id: commentId, text: value, html: value }
          }))
        } else {
          dispatch(setComment({
            network,
            comment: { id: commentId, text: value, html: value }
          }))
        }
      })

    return () => change$.current?.unsubscribe()
  }, [dispatch, network, multiPostId, commentId, delay])

  React.useEffect(() => {
    if (didMountRef.current) {
      change$.current?.next('')
    } else {
      didMountRef.current = true
    }
  }, [composerKey])

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (blockInput()) {
      return
    }
    const value = e.target.value
    const scrollHeight = inputElementRef.current?.scrollHeight
    const clientHeight = inputElementRef.current?.clientHeight

    if (scrollHeight && scrollHeight !== clientHeight) {
      const h = scrollHeight + 2 // EXPL: add 2px to avoid scrollbar
      setHeight(h)
    }
    if (!value) {
      setHeight(DEFAULT_HEIGHT)
    }
    change$.current?.next(value)
  }

  const insertEmoji = React.useCallback((emoji: any) => {
    if (blockInput()) {
      return
    }
    const input = inputElementRef.current
    if (!input) {
      return
    }
    const currentValue = input.value
    // EXPL: the textarea caret lives in selectionStart/selectionEnd and survives losing focus to the
    // emoji picker. Do not read it from the document selection - window.getSelection() never points
    // inside a textarea, so it would always report the picker instead of the caret.
    const insertAtEnd = !wasFocusedRef.current
    const selectionStart = insertAtEnd ? currentValue.length : input.selectionStart
    const selectionEnd = insertAtEnd ? currentValue.length : input.selectionEnd
    const caretPosition = selectionStart + emoji.native.length

    input.value = currentValue.slice(0, selectionStart) + emoji.native + currentValue.slice(selectionEnd)
    // EXPL: use setTimeout to avoid weird bug with resetting selection after change
    setTimeout(() => {
      inputElementRef.current?.setSelectionRange(caretPosition, caretPosition)
    })
    change$.current?.next(input.value)
  }, [blockInput])

  const onDelayChange = (delay: number) => {
    if (multiPostId) {
      dispatch(setMultipostCommentDelay({ postId: multiPostId, commentId, delay }))
      return
    }
    dispatch(setComment({
      network,
      comment: { id: commentId, delay }
    }))
  }
  const onRemoveComment = () => {
    if (multiPostId) {
      dispatch(removeMultipostComment({ postId: multiPostId, commentId }))
      return
    }
    dispatch(removeComment({
      network,
      commentId
    }))
  }
  const charsLeft = maxLength ? maxLength - text.length : undefined
  const helper = charsLeft ? <span>{charsLeft}</span> : null
  // eslint-disable-next-line no-magic-numbers
  const showHelper = charsLeft && charsLeft < 50

  const getPlaceholderKey = () => {
    if (network === BRAND_TWITTER) {
      return isFirstComment ? 'composer.placeholder.comment.twitter' : 'composer.placeholder.comment.twitter.additional'
    }
    return isFirstComment ? 'composer.placeholder.comment' : 'composer.placeholder.comment.additional'
  }

  return (
    <div className={`${styles.box} ${className || ''}`} data-testid="first-comment-textbox" data-network={network}>
      <div className={styles.inputWrap}>
        <textarea
          ref={inputElementRef}
          value={text}
          style={{ height: `${height}px` }}
          data-ig-text
          className={styles.input}
          placeholder={intl.formatMessage({
            id: getPlaceholderKey()
          })}
          onFocus={() => { wasFocusedRef.current = true }}
          onChange={onChange}
        />
        <div className={`${styles.actions} ${props.actionsClassName || ''}`}>
          <EmojiPicker onSelect={insertEmoji} />
          <DelaySelect delay={delay || 0} commentId={commentId} onChange={onDelayChange} />
          {showRemoveButton && (
            <span className={styles['btn-delete']} onClick={onRemoveComment}>
              <Icon path={mdiClose} size="22px" className={styles.icon} />
            </span>
          )}
        </div>
      </div>
      {Boolean(showHelper) && <div className={styles.counter}>{helper}</div>}
    </div>

  )
}

export function DelaySelect({ delay, commentId, className, onChange }: {
  delay: number, commentId: string, className?: string, onChange: (delay: number, id: string) => void
}) {
  const [anchor, setAnchor] = React.useState<HTMLSpanElement | null>(null)
  const presetSelected = isPresetDelay(delay)
  const [customInput, setCustomInput] = React.useState(presetSelected ? '' : String(delay))
  const customInputRef = useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    setCustomInput(isPresetDelay(delay) ? '' : String(delay))
  }, [delay])

  const openPopup = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    setAnchor(e.currentTarget)
  }

  const closePopup = () => {
    setAnchor(null)
  }

  const parsedCustom = Number(customInput)
  const isCustomValid = Number.isInteger(parsedCustom)
    && parsedCustom >= MIN_CUSTOM_DELAY_MINUTES
    && parsedCustom <= MAX_CUSTOM_DELAY_MINUTES
  const showCustomError = customInput !== '' && !isCustomValid

  const onSelectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === CUSTOM_DELAY_VALUE) {
      customInputRef.current?.focus()
      return
    }
    onChange(Number(value), commentId)
  }

  const onCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '')
    setCustomInput(digitsOnly)
  }

  const commitCustom = () => {
    if (!isCustomValid) {
      return
    }
    if (parsedCustom !== delay) {
      onChange(parsedCustom, commentId)
    }
  }

  const onCustomKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitCustom()
    }
  }

  const radioValue = presetSelected ? String(delay) : CUSTOM_DELAY_VALUE
  const tooltip = `Delay: ${formatCommentDelay(delay)}`

  return (
    <div className={className}>
      <Tooltip title={tooltip}>
        <div role="button" className={`${styles['btn-delay']} ${delay !== 0 ? styles.active : ''}`} onClick={openPopup}>
          <Icon path={mdiClockOutline} size="24px" className={styles.icon} />
        </div>
      </Tooltip>
      <Popper
        open={Boolean(anchor)}
        anchorEl={anchor}
        className={styles.popper}
        placement="bottom-end"
      >
        <ClickAwayListener onClickAway={closePopup}>
          <Paper classes={{ root: styles['popup-paper'] }}>
            <h4 className={styles['popup-title']}>
              <span>Delay comment by:</span>
              <Tooltip title="Comment delay is relative to post (for first comment) or to previous comment.">
                <div style={{ height: '20px' }}>
                  <Icon path={mdiInformationOutline} size="20px" className={styles.icon} />
                </div>
              </Tooltip>
            </h4>
            <RadioGroup
              value={radioValue}
              onChange={onSelectionChange}
              className={styles['radio-group']}
            >
              {Object.keys(DELAY_OPTIONS).map((option: keyof typeof DELAY_OPTIONS) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  classes={{ label: styles['radio-label'] }}
                  control={(
                    <Radio
                      className={styles['radio-btn']}
                      color="primary"
                      size="small"
                    />
                  )}
                  label={DELAY_OPTIONS[option]}
                />
              ))}
              <FormControlLabel
                value={CUSTOM_DELAY_VALUE}
                classes={{ label: styles['radio-label'] }}
                control={(
                  <Radio
                    className={styles['radio-btn']}
                    color="primary"
                    size="small"
                  />
                )}
                label={(
                  <span className={styles['custom-row']}>
                    <input
                      ref={customInputRef}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      value={customInput}
                      onChange={onCustomInputChange}
                      onBlur={commitCustom}
                      onKeyDown={onCustomKeyDown}
                      onClick={e => e.stopPropagation()}
                      className={`${styles['custom-input']} ${showCustomError ? styles.error : ''}`}
                      data-testid="comment-delay-custom-input"
                      placeholder="min"
                    />
                    <span className={styles['custom-suffix']}>
                      <span className={styles['custom-upper']}>
                        to {isCustomValid ? parsedCustom + DELAY_WINDOW_MINUTES : 'XX'} min (random time)
                      </span>
                    </span>
                  </span>
                )}
              />
            </RadioGroup>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>

  )
}

export default CommentTextBox
